import { jwtDecode } from "jwt-decode";
import { msalInstance } from "../../../configs";
import { AZURE_ROLES, LOCAL_STORAGE_KEYS } from "../../../constants";

/**
 * Initiates a login process using MSAL's loginPopup method.
 * Stores the access token, role name, and username in local storage upon successful login.
 *
 * @returns {Promise<string|null>} The access token if login is successful, otherwise null.
 */
export const login = async () => {
  try {
    const loginResponse = await msalInstance.loginRedirect({
      scopes: [process.env.REACT_APP_SCOPES], // Requested scopes
    });
    const account = loginResponse.account;
    const accessToken = loginResponse.accessToken;

    // Decode the token to extract additional user information
    const decodedToken = jwtDecode(accessToken);
    const username = decodedToken.name || "Unknown"; // Use 'name' claim or fallback

    // Store user details in local storage
    localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(LOCAL_STORAGE_KEYS.USERNAME, username);

    msalInstance.setActiveAccount(account); // Set active account
    return accessToken;
  } catch (error) {
    console.error("Login failed:", error);
    return null;
  }
};

/**
 * Handles the redirect callback using MSAL's handleRedirectPromise method.
 * Decodes the access token and stores user information in local storage.
 *
 * @returns {Promise<string|null>} The access token if login is successful, otherwise null.
 */
export const handleRedirectCallback = async () => {
  const response = await msalInstance.handleRedirectPromise();
  if (response) {
    const { account, accessToken } = response;

    // Decode token and store user info
    const decodedToken = jwtDecode(accessToken);
    const username = decodedToken.name || "Unknown";

    localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(LOCAL_STORAGE_KEYS.USERNAME, username);

    msalInstance.setActiveAccount(account);
    return accessToken;
  } else {
    console.error("Login failed or cancelled");
  }
};

/**
 * Initiates a logout process using MSAL's logoutPopup method.
 * Clears the access token, role name, and username from local storage upon successful logout.
 *
 * @returns {Promise<boolean>} True if logout is successful, otherwise false.
 */
export const logout = async () => {
  try {
    await msalInstance.logoutPopup({
      postLogoutRedirectUri: process.env.REDIRECT_URI, // Optional: Redirect after logout
    });
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.USERNAME);
    return true;
  } catch (error) {
    console.error("Logout failed", error);
    return false;
  }
};

/**
 * Retrieves the access token for the active account using MSAL's acquireTokenSilent method.
 * Falls back to acquireTokenPopup if silent acquisition fails.
 *
 * @returns {Promise<string|null>} The access token if retrieval is successful, otherwise null.
 */
export const getAccessToken = async () => {
  const activeAccount = msalInstance.getActiveAccount(); // Get the active account

  if (!activeAccount) {
    console.error("No active account! Please log in first.");
    return null;
  }

  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [process.env.REACT_APP_SCOPES], // Scopes for the access token
      account: activeAccount, // Specify the active account
    });

    return tokenResponse.accessToken;
  } catch (error) {
    console.error("Token acquisition error:", error);

    // Optional fallback: Use acquireTokenPopup if silent acquisition fails
    try {
      const tokenResponse = await msalInstance.acquireTokenPopup({
        scopes: [process.env.REACT_APP_SCOPES],
        account: activeAccount,
      });

      return tokenResponse.accessToken;
    } catch (popupError) {
      console.error("Token acquisition (popup) failed:", popupError);
      return null;
    }
  }
};

/**
 * Decodes the access token stored in local storage to extract user roles.
 *
 * @returns {Array<string>|null} An array of roles if decoding is successful, otherwise null.
 */
export const getUserRoles = () => {
  const accessToken = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);

  if (!accessToken) {
    console.error("No access token found. Please log in first.");
    return null;
  }

  try {
    // Decode the access token
    const decodedToken = jwtDecode(accessToken);
    const roles = decodedToken.roles || []; // Roles are typically in the "roles" claim
    return roles;
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

/**
 * Retrieves the role name of the current user based on their assigned roles.
 *
 * @returns {string|null} The role name if roles are found, otherwise null.
 */
export const getUserRoleName = () => {
  const userRoles = getUserRoles();

  if(!userRoles) return null;

  return AZURE_ROLES[userRoles[0]] || null;
};

/**
 * Checks if the user is authenticated by verifying the access token's validity.
 *
 * @returns {boolean} True if the user is authenticated, otherwise false.
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);

  if (!token) return false;

  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds

    // Check if token is expired
    if (decodedToken.exp && decodedToken.exp > currentTime) {
      return true;
    } else {
      // Token expired, clean up local storage
      localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.USERNAME);
      return false;
    }
  } catch (error) {
    console.error("Error decoding token:", error);
    return false;
  }
};
